import { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";

// The brand's colors: PLUS green, blue, lemon, white, orange
const COLORS = ["#57f28b", "#4b10ff", "#62fa95", "#ffffff", "#ff7a00", "#7a3cff"];
const PIECES = 90;
/** How long it rains, the slowest piece included (ms). */
const DURATION = 5200;

const fall = keyframes`
  0% {
    opacity: 1;
    transform: translate3d(0, -10vh, 0) rotate(0deg);
  }
  85% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate3d(var(--drift), 105vh, 0) rotate(var(--spin));
  }
`;

const Layer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  overflow: hidden;
  pointer-events: none;

  span {
    position: absolute;
    top: 0;
    display: block;
    border-radius: 2px;
    animation: ${fall} var(--duration) cubic-bezier(0.25, 0.6, 0.45, 1) var(--delay) both;
  }
`;

/** Confetti falling once over the screen (none for whoever asks for less motion). */
export const Confetti = () => {
  const [isOn, setOn] = useState(() => !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

  useEffect(() => {
    if (!isOn) {
      return;
    }
    const timer = window.setTimeout(() => setOn(false), DURATION);
    return () => window.clearTimeout(timer);
  }, [isOn]);

  // Each piece its own place, size, color, speed and turn
  const pieces = useMemo(
    () =>
      Array.from({ length: PIECES }, (_, index) => {
        const width = 6 + Math.random() * 6;
        return {
          key: index,
          style: {
            left: `${Math.random() * 100}%`,
            width: `${width}px`,
            height: `${width * (0.4 + Math.random() * 0.8)}px`,
            background: COLORS[index % COLORS.length],
            "--drift": `${(Math.random() - 0.5) * 30}vw`,
            "--spin": `${(Math.random() - 0.5) * 1440}deg`,
            "--duration": `${2600 + Math.random() * 1800}ms`,
            "--delay": `${Math.random() * 800}ms`,
          } as React.CSSProperties,
        };
      }),
    []
  );

  if (!isOn) {
    return null;
  }
  return (
    <Layer aria-hidden="true">
      {pieces.map(({ key, style }) => (
        <span key={key} style={style} />
      ))}
    </Layer>
  );
};
